import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Typography } from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import {
  iPromo,
  iTicketingSetup,
  iTicketType,
} from 'services/Ticketing/Ticketing.type';
import {
  card,
  openBlob,
  PAGE_SX,
  primaryButton,
  sectionTitle,
  SwitchField,
  TicketingNav,
} from './common';
import {
  TypeDialog,
  PromoDialog,
  TicketDesignSection,
  VisitorFormSection,
  TicketTypesSection,
  PromoCodesSection,
} from './components';

type Form = Omit<iTicketingSetup['settings'], 'logo_url'>;

export default function TicketingSetup(): JSX.Element {
  const { eventId = '' } = useParams();
  const { ticketingRepository } = useApiContext();
  const [data, setData] = useState<iTicketingSetup | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [lang, setLang] = useState<'fr' | 'en'>('fr');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [editing, setEditing] = useState<Partial<iTicketType> | null>(null);
  const [promo, setPromo] = useState<Partial<iPromo> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = (d: iTicketingSetup): void => {
    setData(d);
    const { logo_url: _logo, ...rest } = d.settings;
    setForm(rest);
  };

  useEffect(() => {
    ticketingRepository
      .getSetup(eventId)
      .then(load)
      .catch((e: Error) => setMessage({ type: 'error', text: e.message }));
  }, [eventId]);

  if (!data || !form) {
    return (
      <Box sx={PAGE_SX}>
        {message ? (
          <Alert severity="error">{message.text}</Alert>
        ) : (
          'Chargement…'
        )}
      </Box>
    );
  }

  const set = <K extends keyof Form>(key: K, value: Form[K]): void =>
    setForm({ ...form, [key]: value });

  const run = (p: Promise<iTicketingSetup>, ok: string): void => {
    setSaving(true);
    p.then((d) => {
      load(d);
      setMessage({ type: 'success', text: ok });
    })
      .catch((e: Error) => setMessage({ type: 'error', text: e.message }))
      .finally(() => setSaving(false));
  };

  const save = (): void =>
    run(
      ticketingRepository.saveSetup(
        eventId,
        form as unknown as Record<string, unknown>
      ),
      'Billet et formulaire enregistrés.'
    );

  const preview = (typeId?: number): void => {
    ticketingRepository
      .previewPdf(eventId, typeId, lang)
      .then((b) => openBlob(b))
      .catch((e: Error) => setMessage({ type: 'error', text: e.message }));
  };

  const publicUrl =
    data.event.company_slug && data.event.slug
      ? `${window.location.origin}/${data.event.company_slug}/tickets/${
          data.event.slug
        }${
          data.event.year &&
          !String(data.event.slug).endsWith(String(data.event.year))
            ? `-${data.event.year}`
            : ''
        }`
      : null;

  return (
    <Box sx={PAGE_SX}>
      <TicketingNav
        current="setup"
        canConfigure
        tickets={data.event.tickets}
        eventName={data.event.name}
      />

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {!data.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Le module Billetterie est désactivé pour ce salon (Paramètres du salon
          » Modules) : la page d&apos;inscription est hors ligne.
        </Alert>
      )}
      {data.killed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          La billetterie est coupée sur tout le serveur (TICKETING_DISABLED).
        </Alert>
      )}

      <Box
        sx={{
          ...card,
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography sx={sectionTitle}>
            Page d&apos;inscription visiteurs
          </Typography>
          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 14, wordBreak: 'break-all' }}
            >
              {publicUrl}
            </a>
          )}
          <Typography
            sx={{
              fontSize: 12,
              color: data.payments_available ? '#666' : '#b45309',
              mt: 0.5,
            }}
          >
            {data.payments_available
              ? 'Paiement en ligne : Stripe.'
              : "Stripe n'est pas configuré sur ce serveur : seuls les billets gratuits et les invitations fonctionnent."}
          </Typography>
        </Box>
        <SwitchField
          label="Inscriptions en ligne ouvertes"
          checked={form.sales_open}
          onChange={(open) => {
            set('sales_open', open);
            run(
              ticketingRepository.saveSetup(eventId, { sales_open: open }),
              open ? 'Inscriptions ouvertes.' : 'Inscriptions fermées.'
            );
          }}
        />
      </Box>

      <TicketTypesSection
        data={data}
        setEditing={setEditing}
        preview={preview}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        <TicketDesignSection
          data={data}
          form={form}
          lang={lang}
          set={set}
          setLang={setLang}
          run={run}
          ticketingRepository={ticketingRepository}
          eventId={eventId}
          preview={preview}
        />

        <VisitorFormSection data={data} form={form} set={set} />
      </Box>

      <Button
        variant="contained"
        disabled={saving}
        onClick={save}
        sx={{ ...primaryButton, mb: 3 }}
      >
        Enregistrer le billet et le formulaire
      </Button>

      <PromoCodesSection data={data} setPromo={setPromo} />

      {editing && (
        <TypeDialog
          type={editing}
          data={data}
          onClose={() => setEditing(null)}
          onSave={(input, id) => {
            run(
              ticketingRepository.saveType(eventId, input, id),
              'Type de billet enregistré.'
            );
            setEditing(null);
          }}
          onDelete={(id) => {
            run(
              ticketingRepository.deleteType(eventId, id),
              "Type de billet supprimé (ou désactivé s'il a déjà des billets)."
            );
            setEditing(null);
          }}
        />
      )}
      {promo && (
        <PromoDialog
          promo={promo}
          data={data}
          onClose={() => setPromo(null)}
          onSave={(input, id) => {
            run(
              ticketingRepository.savePromo(eventId, input, id),
              'Code promo enregistré.'
            );
            setPromo(null);
          }}
          onDelete={(id) => {
            run(
              ticketingRepository.deletePromo(eventId, id),
              "Code promo supprimé (ou désactivé s'il a déjà servi)."
            );
            setPromo(null);
          }}
        />
      )}
    </Box>
  );
}
